import { useEffect } from 'react';
import { AGENTS_BY_ID } from '../../lib/agents';
import type { AgentId, AgentState } from '../../lib/schema';

interface Props {
  readonly agentId: AgentId;
  readonly state: AgentState;
  readonly onClose: () => void;
}

export function RoomDetailPanel({ agentId, state, onClose }: Props) {
  const cfg = AGENTS_BY_ID[agentId];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!cfg) return null;

  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-labelledby={`detail-${agentId}`}
      className="fixed top-0 right-0 bottom-0 w-[420px] max-w-[90vw] z-50"
      style={{
        background: 'rgba(14, 32, 36, 0.96)',
        borderLeft: '1px solid var(--color-deck-grid)',
        padding: '28px 28px 20px',
        overflowY: 'auto',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.28em] mb-1"
            style={{
              color: cfg.color,
              fontFamily: 'DepartureMono, monospace',
            }}
          >
            {cfg.title} · {cfg.businessUnit}
          </div>
          <h2
            id={`detail-${agentId}`}
            className="text-3xl"
            style={{
              fontFamily: 'DepartureMono, Press Start 2P, monospace',
              color: 'var(--color-deck-ink)',
              letterSpacing: '0.02em',
            }}
          >
            {cfg.label}
          </h2>
        </div>
        <button
          onClick={onClose}
          aria-label="Close panel"
          className="rounded-sm p-2 hover:bg-white/5 transition-colors"
          style={{
            color: 'var(--color-deck-muted)',
            border: '1px solid var(--color-deck-grid)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 12,
          }}
        >
          ESC
        </button>
      </div>

      <section className="mb-6">
        <h3
          className="text-[10px] uppercase tracking-[0.24em] mb-2"
          style={{ color: 'var(--color-deck-muted)', fontFamily: 'JetBrains Mono, monospace' }}
        >
          Dossier
        </h3>
        <p
          style={{
            color: 'var(--color-deck-ink)',
            fontSize: 14,
            lineHeight: 1.6,
            fontFamily: 'Space Grotesk, system-ui, sans-serif',
          }}
        >
          {cfg.dossier}
        </p>
        <a
          href={`/agents/${cfg.id}`}
          className="inline-block mt-3 text-[11px] uppercase tracking-[0.2em] hover:underline"
          style={{
            color: 'var(--color-deck-cyan)',
            fontFamily: 'JetBrains Mono, monospace',
          }}
        >
          Full dossier →
        </a>
      </section>

      <section className="mb-6">
        <h3
          className="text-[10px] uppercase tracking-[0.24em] mb-2"
          style={{ color: 'var(--color-deck-muted)', fontFamily: 'JetBrains Mono, monospace' }}
        >
          Live status
        </h3>
        <div
          className="px-3 py-2 rounded-sm"
          style={{
            background: 'var(--color-deck-surface)',
            border: '1px solid var(--color-deck-grid)',
          }}
        >
          <div
            className="text-[11px] uppercase tracking-[0.2em] mb-1"
            style={{
              color:
                state.status === 'offline'
                  ? 'var(--color-deck-magenta)'
                  : state.status === 'celebrating'
                    ? 'var(--color-deck-success)'
                    : state.status === 'working'
                      ? 'var(--color-deck-mint)'
                      : 'var(--color-deck-amber)',
              fontFamily: 'DepartureMono, monospace',
            }}
          >
            ● {state.status}
          </div>
          <div
            style={{
              color: 'var(--color-deck-ink)',
              fontSize: 13,
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            {state.activity}
          </div>
        </div>
      </section>

      {state.kpis && (
        <section className="mb-6">
          <h3
            className="text-[10px] uppercase tracking-[0.24em] mb-3"
            style={{ color: 'var(--color-deck-muted)', fontFamily: 'JetBrains Mono, monospace' }}
          >
            KPIs
          </h3>
          <ul className="space-y-2">
            {Object.entries(state.kpis).map(([k, v]) => (
              <li
                key={k}
                className="flex items-baseline justify-between pb-2"
                style={{ borderBottom: '1px dashed var(--color-deck-grid)' }}
              >
                <span
                  className="text-[11px] uppercase tracking-wider"
                  style={{
                    color: 'var(--color-deck-muted)',
                    fontFamily: 'JetBrains Mono, monospace',
                  }}
                >
                  {k}
                </span>
                <span
                  className="tabular-nums"
                  style={{
                    color: 'var(--color-deck-ink)',
                    fontFamily: 'DepartureMono, monospace',
                    fontSize: 16,
                  }}
                >
                  {v.toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h3
          className="text-[10px] uppercase tracking-[0.24em] mb-2"
          style={{ color: 'var(--color-deck-muted)', fontFamily: 'JetBrains Mono, monospace' }}
        >
          Runtime
        </h3>
        <div
          className="text-[11px] break-all"
          style={{ color: 'var(--color-deck-ink)', fontFamily: 'JetBrains Mono, monospace' }}
        >
          {cfg.statusUrl}
        </div>
        {state.meta?.simulated && (
          <div
            className="mt-3 inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm"
            style={{
              color: 'var(--color-deck-magenta)',
              border: '1px solid var(--color-deck-magenta)',
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            dev mock
          </div>
        )}
      </section>
    </aside>
  );
}
